import { chunk, debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { type Canvases } from '@air/api';
import { reportErrorToBugsnag } from '@air/utils-error';

import { useEditCanvasStore } from '../../store';
import { type AppNode } from '../../types';
import { getNodeData, type NodeSyncData } from './getNodeData';
import {
  type CreateOperation,
  type DeleteOperation,
  type NodeUpdate,
  type SyncOperation,
  squashQueue,
  type UpdateOperation,
} from './squashQueue';

export type { NodeUpdate } from './squashQueue';

const DEBOUNCE_MS = 500;
const BATCH_SIZE = 100;

export type CreateCanvasNodesParams = NonNullable<Parameters<typeof Canvases.Nodes.create>[0]>;
export type UpdateCanvasNodesParams = NonNullable<Parameters<typeof Canvases.Nodes.update>[0]>;
export type DeleteCanvasNodesParams = NonNullable<Parameters<typeof Canvases.Nodes.delete>[0]>;

export type RenameCanvasParams = {
  canvasId: string;
  title: string;
};

export type ResolveCanvasIdResult = {
  canvasId: string;
  /** Local node IDs that were created alongside the canvas (bundled in the create call) */
  createdNodeLocalIds?: string[];
};

export type PendingCreateNode = {
  localId: string;
  params: {
    data: NodeSyncData;
    height: number;
    name: string;
    position: { x: number; y: number };
    width: number;
    zIndex: string;
  };
};

export type CanvasSyncMutations = {
  workspaceId: string;
  canvasId: string;
  createNodes: (params: CreateCanvasNodesParams & { skipInvalidation?: boolean }) => Promise<{
    nodes: { id: string }[];
  }>;
  updateNodes: (params: UpdateCanvasNodesParams) => Promise<unknown>;
  deleteNodes: (params: DeleteCanvasNodesParams & { skipInvalidation?: boolean }) => Promise<unknown>;
  renameCanvas: (params: RenameCanvasParams) => Promise<unknown>;
  onGroupSync?: () => void;
  /** Called at flush time when canvasId is empty (draft mode). Receives pending node creates to bundle with canvas creation. */
  resolveCanvasId?: (pendingCreates: PendingCreateNode[]) => Promise<ResolveCanvasIdResult>;
};

export type GroupMutations = {
  createGroup: (params: { nodeIds: string[] }) => Promise<{
    groupNode: { id: string };
  }>;
  reactivateGroup: (params: { groupNodeId: string; nodeIds: string[] }) => Promise<void>;
  deleteGroup: (params: { groupNodeId: string }) => Promise<void>;
};

/**
 * Manages a debounced queue of canvas node sync operations.
 * Operations are accumulated in an array and squashed on flush
 * (latest wins per node). A create followed by a delete cancels both.
 * Operations are sent to the server in batches of up to 100.
 * Each batch is all-or-nothing: if any node in a batch fails, the entire batch is rejected.
 */
export function useCanvasSync(mutations: CanvasSyncMutations) {
  const queueRef = useRef<SyncOperation[]>([]);
  const mutationsRef = useRef(mutations);
  mutationsRef.current = mutations;

  // Serialize group API calls so rapid undo/redo doesn't race (e.g., delete
  // before create finishes). After each call (success or error), trigger a
  // debounced Canvases.get re-sync so rapid operations produce one refetch.
  const groupApiQueueRef = useRef<Promise<unknown>>(Promise.resolve());

  const debouncedGroupSyncRef = useRef(
    debounce(() => {
      mutationsRef.current.onGroupSync?.();
    }, DEBOUNCE_MS),
  );

  useEffect(() => {
    return () => debouncedGroupSyncRef.current.cancel();
  }, []);

  const serializedGroupMutations: GroupMutations = useMemo(() => {
    const enqueue = <T>(fn: () => Promise<T>): Promise<T> => {
      const call = groupApiQueueRef.current.then(fn);
      groupApiQueueRef.current = call
        .then(() => {
          debouncedGroupSyncRef.current();
        })
        .catch(() => {
          debouncedGroupSyncRef.current();
        });
      return call;
    };

    return {
      createGroup: (params) =>
        enqueue(async () => {
          const { workspaceId, canvasId, createNodes: create } = mutationsRef.current;
          const result = await create({
            canvasId,
            nodes: [
              {
                childNodeIds: params.nodeIds,
                data: {
                  type: 'group',
                },
                height: 0,
                name: 'group',
                position: { x: 0, y: 0 },
                width: 0,
                zIndex: '0',
              },
            ],
            skipInvalidation: true,
            workspaceId,
          });

          const groupNode = result.nodes[0];

          if (!groupNode) {
            throw new Error('Server returned no group node');
          }

          return { groupNode: { id: groupNode.id } };
        }),
      deleteGroup: (params) =>
        enqueue(async () => {
          const { workspaceId, canvasId, deleteNodes: del } = mutationsRef.current;
          await del({ canvasId, nodeIds: [params.groupNodeId], skipInvalidation: true, workspaceId });
        }),
      reactivateGroup: (params) =>
        enqueue(async () => {
          const { workspaceId, canvasId, updateNodes: update } = mutationsRef.current;
          await update({
            canvasId,
            nodes: [
              {
                childNodeIds: params.nodeIds,
                nodeId: params.groupNodeId,
              },
            ],
            workspaceId,
          });
        }),
    };
  }, []);

  const flushRef = useRef(
    debounce(async () => {
      const operations = squashQueue(queueRef.current);
      queueRef.current = [];

      if (operations.length === 0) {
        return;
      }

      const {
        workspaceId,
        createNodes: createNodesMutation,
        updateNodes: updateNodesMutation,
        deleteNodes: deleteNodesMutation,
      } = mutationsRef.current;

      let canvasId = mutationsRef.current.canvasId;

      const storeNodes = useEditCanvasStore.getState().nodes;

      // Partition operations early so we can pass pending creates to resolveCanvasId
      let creates: CreateOperation[] = [];
      const updates: UpdateOperation[] = [];
      const deletes: DeleteOperation[] = [];

      for (const op of operations) {
        if (op.type === 'create') {
          creates.push(op);
        } else if (op.type === 'update') {
          updates.push(op);
        } else {
          deletes.push(op);
        }
      }

      // Draft mode: resolve canvas ID, bundling pending node creates with canvas creation
      if (!canvasId && mutationsRef.current.resolveCanvasId) {
        const pendingCreates: PendingCreateNode[] = creates
          .filter((op) => !op.node.remoteId)
          .map((op) => {
            const liveNode = storeNodes.find((n) => n.id === op.node.id) ?? op.node;
            const nodeData = getNodeData(liveNode);

            if (!nodeData) {
              return undefined;
            }

            return {
              localId: op.node.id,
              params: {
                data: nodeData.data,
                height: typeof liveNode.style?.height === 'number' ? liveNode.style.height : 0,
                name: nodeData.name,
                position: liveNode.position,
                width: typeof liveNode.style?.width === 'number' ? liveNode.style.width : 0,
                zIndex: String(liveNode.zIndex ?? 0),
              },
            };
          })
          .filter((c): c is PendingCreateNode => c != null);

        try {
          const result = await mutationsRef.current.resolveCanvasId(pendingCreates);
          canvasId = result.canvasId;

          // Remove creates that were bundled with canvas creation
          if (result.createdNodeLocalIds && result.createdNodeLocalIds.length > 0) {
            const handledIds = new Set(result.createdNodeLocalIds);
            creates = creates.filter((op) => !handledIds.has(op.node.id));
          }
        } catch (error) {
          // Canvas creation failed — queued operations are lost (same as existing behavior for failed flushes).
          reportErrorToBugsnag({
            context: 'Failed to resolve canvas ID during sync flush',
            error,
          });

          return;
        }
      }

      if (!workspaceId || !canvasId) {
        return;
      }

      if (creates.length > 0) {
        // Nodes with a remoteId were soft-deleted and need reactivation via
        // update, not a fresh create. This happens when undo re-adds a
        // previously removed node.
        const trueCreates: CreateOperation[] = [];
        const reactivations: CreateOperation[] = [];
        for (const op of creates) {
          if (op.node.remoteId) {
            reactivations.push(op);
          } else {
            trueCreates.push(op);
          }
        }

        if (reactivations.length > 0) {
          const reactivationUpdates = reactivations
            .map((op) => {
              const liveNode = storeNodes.find((n) => n.id === op.node.id);
              const resolvedNode = liveNode ?? op.node;
              const nodeData = getNodeData(resolvedNode);

              if (!nodeData || !op.node.remoteId) {
                return undefined;
              }

              return {
                data: nodeData.data,
                height: typeof resolvedNode.style?.height === 'number' ? resolvedNode.style.height : 0,
                nodeId: op.node.remoteId,
                position: resolvedNode.position,
                width: typeof resolvedNode.style?.width === 'number' ? resolvedNode.style.width : 0,
                zIndex: String(resolvedNode.zIndex ?? 0),
              };
            })
            .filter((u): u is NonNullable<typeof u> => u != null);

          for (const batch of chunk(reactivationUpdates, BATCH_SIZE)) {
            try {
              await updateNodesMutation({ canvasId, nodes: batch, workspaceId });
            } catch {
              // handled by the mutation
            }
          }
        }

        const validCreates = trueCreates
          .map((op) => {
            // Use live store node — see "Live state resolution for CREATEs" in CLAUDE.md
            const liveNode = storeNodes.find((n) => n.id === op.node.id);
            const resolvedNode = liveNode ?? op.node;
            const nodeData = getNodeData(resolvedNode);

            if (!nodeData) {
              return undefined;
            }

            return {
              localId: op.node.id,
              params: {
                data: nodeData.data,
                height: typeof resolvedNode.style?.height === 'number' ? resolvedNode.style.height : 0,
                name: nodeData.name,
                position: resolvedNode.position,
                width: typeof resolvedNode.style?.width === 'number' ? resolvedNode.style.width : 0,
                zIndex: String(resolvedNode.zIndex ?? 0),
              },
            };
          })
          .filter((c): c is NonNullable<typeof c> => c != null);

        for (const batch of chunk(validCreates, BATCH_SIZE)) {
          try {
            const result = await createNodesMutation({
              canvasId,
              nodes: batch.map((c) => c.params),
              workspaceId,
            });

            result.nodes.forEach((remoteNode, i) => {
              useEditCanvasStore.getState().updateNodeRemoteId(batch[i].localId, remoteNode.id);
            });
          } catch {
            // handled by the mutation
          }
        }
      }

      if (updates.length > 0) {
        // Re-read store nodes to pick up remoteIds assigned by CREATEs above
        const storeNodesForUpdates = useEditCanvasStore.getState().nodes;
        const validUpdates = updates
          .map((op) => {
            const remoteId = storeNodesForUpdates.find((n) => n.id === op.update.id)?.remoteId;

            if (!remoteId) {
              return undefined;
            }

            return {
              nodeId: remoteId,
              ...(op.update.position && { position: op.update.position }),
              ...(op.update.width != null && { width: op.update.width }),
              ...(op.update.height != null && { height: op.update.height }),
              ...(op.update.zIndex != null && { zIndex: String(op.update.zIndex) }),
              ...(op.update.data && { data: op.update.data }),
            };
          })
          .filter((u): u is NonNullable<typeof u> => u != null);

        for (const batch of chunk(validUpdates, BATCH_SIZE)) {
          try {
            await updateNodesMutation({ canvasId, nodes: batch, workspaceId });
          } catch {
            // handled by the mutation
          }
        }
      }

      if (deletes.length > 0) {
        // Re-read store nodes to pick up remoteIds assigned by CREATEs above
        const storeNodesForDeletes = useEditCanvasStore.getState().nodes;
        const validNodeIds = deletes
          .map((op) => op.node.remoteId ?? storeNodesForDeletes.find((n) => n.id === op.node.id)?.remoteId)
          .filter((id): id is string => !!id);

        for (const batch of chunk(validNodeIds, BATCH_SIZE)) {
          try {
            await deleteNodesMutation({ canvasId, nodeIds: batch, workspaceId });
          } catch {
            // handled by the mutation
          }
        }
      }
    }, DEBOUNCE_MS),
  );

  useEffect(() => {
    return () => flushRef.current.cancel();
  }, []);

  const createNodes = useCallback((nodes: AppNode[]): void => {
    for (const node of nodes) {
      queueRef.current.push({ node, type: 'create' });
    }

    flushRef.current();
  }, []);

  const updateNodes = useCallback((updates: NodeUpdate[]): void => {
    for (const update of updates) {
      queueRef.current.push({ type: 'update', update });
    }

    flushRef.current();
  }, []);

  const deleteNodes = useCallback((nodes: AppNode[]): void => {
    for (const node of nodes) {
      queueRef.current.push({ node, type: 'delete' });
    }

    flushRef.current();
  }, []);

  const renameCanvas = useCallback((title: string) => {
    const { canvasId, renameCanvas: rename } = mutationsRef.current;

    return rename({ canvasId, title });
  }, []);

  const flushSync = useCallback((): Promise<void> => {
    return flushRef.current.flush() ?? Promise.resolve();
  }, []);

  return { createNodes, deleteNodes, flushSync, groupMutations: serializedGroupMutations, renameCanvas, updateNodes };
}
